<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import MessageReplyPreview from 'shared/components/MessageReplyPreview.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const { variant, orientation, inReplyTo, shouldGroupWithNext } =
  useMessageContext();
const { t } = useI18n();

const bubbleLinkClass =
  '[&_.prose-bubble_a]:!text-current [&_.prose-bubble_a]:opacity-80 [&_.prose-bubble_a]:underline [&_.prose-bubble_a]:decoration-current/60 [&_.prose-bubble_a]:underline-offset-2 hover:[&_.prose-bubble_a]:opacity-90 hover:[&_.prose-bubble_a]:decoration-current/80';

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: `bg-woot-600 text-[var(--n-brand-contrast)] ${bubbleLinkClass}`,
  [MESSAGE_VARIANTS.PRIVATE]: `bg-n-brand/20 text-n-slate-12 ring-1 ring-n-brand/50 [&_.prosemirror-mention-node]:font-semibold ${bubbleLinkClass}`,
  [MESSAGE_VARIANTS.USER]: `bg-n-slate-4 text-n-slate-12 ${bubbleLinkClass}`,
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: `bg-n-brand/10 text-n-slate-12 ring-1 ring-n-brand/30 ${bubbleLinkClass}`,
  [MESSAGE_VARIANTS.TEMPLATE]: `bg-woot-600 text-[var(--n-brand-contrast)] ${bubbleLinkClass}`,
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const hasBrandBubbleContent = computed(() =>
  [MESSAGE_VARIANTS.AGENT, MESSAGE_VARIANTS.TEMPLATE].includes(variant.value)
);

const replyPreviewVariant = computed(() =>
  hasBrandBubbleContent.value ? 'brand' : 'neutral'
);

const metaTextClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return 'text-n-slate-11';
  if (hasBrandBubbleContent.value) return 'text-white/85';

  return 'text-n-slate-11';
});

const metaChipClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'bg-n-brand/10 text-n-slate-11 ring-1 ring-n-brand/20';
  }

  if (hasBrandBubbleContent.value) {
    return 'bg-white/10 text-white/85 ring-1 ring-white/10';
  }

  return 'bg-n-alpha-2 text-n-slate-11';
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

const attachmentReplyPreview = fileType => {
  const attachmentLabels = {
    image: t('CHAT_LIST.ATTACHMENTS.image.CONTENT'),
    audio: t('CHAT_LIST.ATTACHMENTS.audio.CONTENT'),
    video: t('CHAT_LIST.ATTACHMENTS.video.CONTENT'),
    file: t('CHAT_LIST.ATTACHMENTS.file.CONTENT'),
    location: t('CHAT_LIST.ATTACHMENTS.location.CONTENT'),
    ig_reel: t('CHAT_LIST.ATTACHMENTS.ig_reel.CONTENT'),
    fallback: t('CHAT_LIST.ATTACHMENTS.fallback.CONTENT'),
    contact: t('CHAT_LIST.ATTACHMENTS.contact.CONTENT'),
    embed: t('CHAT_LIST.ATTACHMENTS.embed.CONTENT'),
  };

  return attachmentLabels[fileType] || attachmentLabels.fallback;
};

const replyToPreview = computed(() => {
  if (!inReplyTo.value) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).plainText.trim();
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return attachmentReplyPreview(fileType);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <MessageReplyPreview
      v-if="inReplyTo"
      :label="t('CONVERSATION.REPLYBOX.REPLYING_TO')"
      :content="replyToPreview"
      :variant="replyPreviewVariant"
      class="mb-2"
      @navigate="scrollToMessage"
    />
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :meta-chip-class="metaChipClass"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        metaTextClass,
      ]"
      class="mt-2"
    />
  </div>
</template>
